import type { StrictOmit } from '@enmon/commons';
import type {
  Meter,
  MeterDetailElectricity,
  MeterDetailGas,
  MeterDetailThermal,
  MeterDetailWater,
} from '@enmon/commons/lib/enmon';

import { defineMessages } from '@/components/formatted_message/FormattedMessage';
import { APP_DICTIONARY } from '../root';

type TranslatedMeterKeys =
  | keyof StrictOmit<
      Meter,
      | 'accessibility'
      | 'createdAt'
      | 'detail'
      | 'location'
      | 'updatedAt'
      | 'statistics'
      | 'floorPlanLocation'
      | 'ownerType'
    >
  | 'latitude'
  | 'longitude'
  | 'owner'
  | 'devEUI'
  | 'multiplier'
  | 'offset'
  | 'pulses'
  | 'unit';

export const meterFieldsTranslated = defineMessages<TranslatedMeterKeys>({
  _id: { id: 'app.common.meter.fields.id' },
  apiKey: { id: 'app.common.meter.fields.apiKey' },
  avatarFile: { id: 'app.common.meter.fields.avatarFile' },
  buildingFloor: { id: 'app.common.meter.fields.buildingFloor' },
  businessHours: APP_DICTIONARY.businessHours,
  calibrated: { id: 'app.common.meter.fields.calibrated' },
  code: { id: 'app.common.meter.fields.code' },
  toolsId: { id: 'app.common.meter.fields.toolsId' },
  customer: { id: 'app.common.meter.fields.customer' },
  devEUI: { id: 'app.common.meter.fields.devEUI' },
  establishment: APP_DICTIONARY.establishment,
  latitude: { id: 'app.common.meter.fields.location.latitude' },
  longitude: { id: 'app.common.meter.fields.location.longitude' },
  manufactured: { id: 'app.common.meter.fields.manufactured' },
  manufacturer: { id: 'app.common.meter.fields.manufacturer' },
  meterModel: { id: 'app.common.meter.fields.meterModel' },
  multiplier: { id: 'app.common.meter.fields.multiplier' },
  name: APP_DICTIONARY.name,
  note: APP_DICTIONARY.note,
  offset: { id: 'app.common.meter.fields.offset' },
  owner: { id: 'app.common.meter.fields.owner' },
  placement: { id: 'app.common.meter.fields.placement' },
  parent: { id: 'app.common.meter.fields.parent' },
  pulses: { id: 'app.common.meter.fields.pulses' },
  commodity: APP_DICTIONARY.commodity,
  rssi: { id: 'app.common.meter.fields.rssi' },
  serial: APP_DICTIONARY.serial,
  unit: APP_DICTIONARY.unit,
  supplyPoint: APP_DICTIONARY.supplyPoint,
  registers: APP_DICTIONARY.registers,
  license: { id: 'app.common.meter.fields.license' },
  battery: { id: 'app.common.meter.fields.battery' },
  lastUpdate: { id: 'app.common.meter.fields.lastUpdate' },
  lastManualUpdate: { id: 'app.common.meter.fields.lastManualUpdate' },
  inactiveThresholdHours: { id: 'app.common.meter.fields.inactiveThresholdHours' },
  isActive: { id: 'app.common.meter.fields.isActive' },
  kind: { id: 'app.establishments.detail.FormMeter.kind' },
  deactivatedAt: { id: 'app.common.meter.fields.deactivatedAt' },
  activatedAt: { id: 'app.common.meter.fields.activatedAt' },
});

type MeterDetailKey =
  | keyof MeterDetailElectricity
  | keyof MeterDetailThermal
  | keyof MeterDetailGas
  | keyof MeterDetailWater;

export const meterDetailFieldsTranslated = defineMessages<MeterDetailKey>({
  deviceCategory: {
    id: 'app.common.meter.detail.fields.deviceCategory',
  },
  diameterNominal: {
    id: 'app.common.meter.detail.fields.diameterNominal',
  },
  flowrate: {
    id: 'app.common.meter.detail.fields.flowrate',
  },
  layLength: {
    id: 'app.common.meter.detail.fields.layLength',
  },
  orientation: {
    id: 'app.common.meter.detail.fields.orientation',
  },
  flange: {
    id: 'app.common.meter.detail.fields.flange',
  },
  thermalMeasurementType: {
    id: 'app.common.meter.detail.fields.thermalMeasurementType',
  },
  hotWater: {
    id: 'app.common.meter.detail.fields.hotWater',
  },
  gSize: {
    id: 'app.common.meter.detail.fields.gSize',
  },
  pMax: {
    id: 'app.common.meter.detail.fields.pMax',
  },
  qMax: {
    id: 'app.common.meter.detail.fields.qMax',
  },
  qMin: {
    id: 'app.common.meter.detail.fields.qMin',
  },
  isDirect: {
    id: 'app.common.meter.detail.fields.isDirect',
  },
  measurementCategory: {
    id: 'app.common.meter.detail.fields.measurementCategory',
  },
  measuringCurrentTransformer: {
    id: 'app.common.meter.detail.fields.measuringCurrentTransformer',
  },
  phases: {
    id: 'app.common.meter.detail.fields.phases',
  },
  mounting: {
    id: 'app.common.meter.detail.fields.mounting',
  },
  width: {
    id: 'app.common.meter.detail.fields.width',
  },
  tariffs: {
    id: 'app.common.meter.detail.fields.tariffs',
  },
  installationPosition: {
    id: 'app.common.meter.detail.fields.installationPosition',
  },
  resistance: {
    id: 'app.common.meter.detail.fields.resistance',
  },
  circuitBreakerSize: {
    id: 'app.common.meter.detail.fields.circuitBreakerSize',
  },
  iMax: {
    id: 'app.common.meter.detail.fields.iMax',
  },
  iMin: {
    id: 'app.common.meter.detail.fields.iMin',
  },
  iRef: {
    id: 'app.common.meter.detail.fields.iRef',
  },
  inputSpec: {
    id: 'app.common.meter.detail.fields.inputSpec',
  },
  measurementType: {
    id: 'app.common.meter.detail.fields.measurementType',
  },
  woltman: {
    id: 'app.common.meter.detail.fields.woltman',
  },
  tubeMaterial: {
    id: 'app.common.meter.detail.fields.tubeMaterial',
  },
  secondSerialNumber: {
    id: 'app.common.meter.detail.fields.secondSerialNumber',
  },
});
